import React, { useEffect, useState } from "react";
import { Receipt, Loader2, RefreshCw, BadgeCheck, Gavel, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import { adminApi, errorMessage, formatDate, titleCase } from "../../lib/api";

const TABS = ["open", "resolved"];

export default function CancellationDisputes() {
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  const load = () => {
    setData(null);
    adminApi.cancellationDisputes(status).then((d) => { setData(d.disputes || []); setCounts(d.counts || {}); }).catch((e) => { toast.error(errorMessage(e)); setData([]); });
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (feeId, action) => {
    const note = (notes[feeId] || "").trim();
    if (action === "uphold" && note.length < 4) { toast.error("Add a short note so the rider knows why the fee stands."); return; }
    setBusy(feeId + action);
    try {
      await adminApi.resolveCancellationFee(feeId, action, note || (action === "waive" ? "Waived after review" : "Fee upheld after review"));
      toast.success(action === "waive" ? "Fee waived — rider notified" : "Fee upheld — rider notified");
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  return (
    <>
      <PageHeader title="Fee disputes" description="Riders' disputes on cancellation fees. Waive to drop the charge, or uphold if the fee was fair." actions={<Button variant="outline" onClick={load} className="rounded-full" data-testid="disputes-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>} />
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        {TABS.map((s) => <button key={s} onClick={() => setStatus(s)} className={`text-left ${status === s ? "ring-2 ring-brand rounded-2xl" : ""}`} data-testid={`disputes-tab-${s}`}><StatCard label={titleCase(s)} value={counts[s] ?? "…"} icon={Receipt} tone={s === "open" ? "brand" : "graphite"} /></button>)}
      </div>
      {data === null && <LoadingBlock rows={4} />}
      {data && data.length === 0 && <EmptyState icon={Receipt} title={`No ${status} disputes`} text="Nothing to review here." />}
      {data && data.length > 0 && (
        <div className="space-y-4" data-testid="disputes-list">
          {data.map((f) => (
            <div key={f.fee_id} className="kt-card p-5" data-testid={`dispute-${f.fee_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{f.reason === "no_show" ? "No-show fee" : "Rider cancellation"}</span>
                    ₹{f.amount}
                    {f.status === "waived" && <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">Waived</span>}
                    {f.status === "refunded" && <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">Refunded</span>}
                  </p>
                  <p className="mt-1 text-xs text-ink-soft">
                    By {f.rider?.name || "Rider"}{f.rider?.email ? ` · ${f.rider.email}` : ""}{f.rider?.phone ? ` · ${f.rider.phone}` : ""} · disputed {formatDate(f.disputed_at)}
                  </p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {f.pickup_address ? `${f.pickup_address} → ${f.drop_address || "—"}` : "Ride"} · ₹{f.flat_fee} base{f.wait_charge > 0 ? ` + ₹${f.wait_charge} waiting (${f.wait_min} min)` : ""} · fee charged {formatDate(f.created_at)}
                  </p>
                  {f.dispute_reason && <p className="mt-2 rounded-xl bg-surface-100 p-3 text-sm text-ink-soft" data-testid={`dispute-reason-${f.fee_id}`}><span className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Rider's reason</span><br />{f.dispute_reason}</p>}
                  {f.dispute_photo_ids?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`dispute-evidence-${f.fee_id}`}>
                      {f.dispute_photo_ids.map((pid) => (
                        <a key={pid} href={adminApi.feeDisputePhotoUrl(pid)} target="_blank" rel="noreferrer" data-testid={`dispute-evidence-${pid}`}>
                          <img src={adminApi.feeDisputePhotoUrl(pid)} alt="evidence" className="h-20 w-20 rounded-xl border border-surface-200 object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  {f.appealed && <p className="mt-2 rounded-xl bg-blue-50 p-3 text-sm text-blue-800" data-testid={`appeal-reason-${f.fee_id}`}><span className="text-[10px] font-bold uppercase tracking-wider text-blue-500">Rider's appeal</span><br />{f.appeal_reason}</p>}
                  {f.audit?.length > 0 && (
                    <div className="mt-2 border-t border-surface-200 pt-2" data-testid={`audit-${f.fee_id}`}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Audit trail</p>
                      <ul className="mt-1 space-y-0.5 text-xs text-ink-soft">
                        {f.audit.map((a, i) => (
                          <li key={i} data-testid={`audit-row-${f.fee_id}-${i}`}>
                            <span className="font-semibold">{titleCase(a.action)}</span> by {a.by_name}{a.by_role ? ` (${a.by_role})` : ""} · {formatDate(a.at)}{a.note ? ` — “${a.note}”` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {f.dispute_status === "open" ? (
                  <div className="flex w-full flex-col gap-2 sm:w-64">
                    {f.appealed && <span className="self-start rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700" data-testid={`appeal-badge-${f.fee_id}`}>APPEAL — second look</span>}
                    <textarea value={notes[f.fee_id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [f.fee_id]: e.target.value }))} rows={2} placeholder="Note to rider (required to uphold)" className="w-full rounded-lg border border-surface-300 p-2 text-xs" data-testid={`resolve-note-${f.fee_id}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => resolve(f.fee_id, "waive")} disabled={!!busy} className="rounded-full bg-green-600 text-white hover:bg-green-700" data-testid={`dispute-waive-${f.fee_id}`}>{busy === f.fee_id + "waive" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BadgeCheck className="mr-2 h-4 w-4" />} Waive</Button>
                      <Button size="sm" variant="outline" onClick={() => resolve(f.fee_id, "uphold")} disabled={!!busy} className="rounded-full" data-testid={`dispute-uphold-${f.fee_id}`}>{busy === f.fee_id + "uphold" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gavel className="mr-2 h-4 w-4" />} Uphold</Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-right">
                    {f.final && <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-graphite/10 px-2 py-0.5 text-[10px] font-bold text-graphite" data-testid={`dispute-final-${f.fee_id}`}><Lock className="h-3 w-3" /> FINAL — closed</span>}
                    <p className="text-xs font-semibold text-ink-soft" data-testid={`dispute-resolution-${f.fee_id}`}>{titleCase(f.resolution || "Resolved")}{f.resolved_by_name ? ` by ${f.resolved_by_name}` : ""}</p>
                    {f.resolved_at && <p className="text-[11px] text-ink-muted" data-testid={`dispute-resolved-at-${f.fee_id}`}>{formatDate(f.resolved_at)}</p>}
                    {f.appeal_window_closed && <p className="text-[11px] font-semibold text-ink-muted" data-testid={`dispute-appeal-closed-${f.fee_id}`}>Appeal window closed</p>}
                    {f.resolution_note && <p className="mt-1 max-w-xs text-xs text-ink-muted">“{f.resolution_note}”</p>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
